import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../api/client'
import { useResource, useToast } from '../api/hooks'
import type { Door, DoorListItem, DoorScheduleBand, DoorStatus, Group } from '../api/types'
import { PageHeader } from '../components/Layout'
import { Alert, Card, ConfirmDialog, DoorStatusBadge, ErrorBox, Field, GroupChips, Spinner, Toast, describeError, formatDateTime, relative } from '../components/Ui'
import { de, fmt } from '../i18n/de'
import { DoorScheduleBands } from './Groups'

const CODE_KEY_LENGTH = 64

export function DoorsPage() {
  const navigate = useNavigate()
  const doors = useResource<DoorListItem[]>('/doors')
  const [showCreate, setShowCreate] = useState(false)
  const { toast, show, clear } = useToast()

  if (doors.loading) return <Spinner />
  if (doors.error) return <ErrorBox error={doors.error} onRetry={doors.reload} />

  return (
    <>
      <PageHeader title={de.doors.title}
                  actions={<button className="btn btn-primary" onClick={() => setShowCreate(true)}>
                    {de.doors.create}
                  </button>} />

      <div className="card">
        <table>
          <thead>
            <tr>
              <th style={{ width: 150 }}>{de.doors.columnStatus}</th>
              <th>{de.doors.columnName}</th>
              <th>{de.doors.columnLocation}</th>
              <th>{de.doors.columnGroups}</th>
              <th className="num">{de.doors.columnCodes}</th>
              <th>{de.doors.columnFirmware}</th>
            </tr>
          </thead>
          <tbody>
            {(doors.data ?? []).map(door => (
              <>
                <tr key={door.id} className="clickable" onClick={() => navigate(`/doors/${door.id}`)}>
                  <td><DoorStatusBadge status={door.status} lastSeenAt={door.lastSeenAt} /></td>
                  <td><strong>{door.name}</strong></td>
                  <td className="muted">{door.location ?? de.app.none}</td>
                  <td><GroupChips groups={door.groups} max={4} /></td>
                  <td className="num">
                    {door.deliveredCodeCount} <span className="faint">/ 500</span>
                  </td>
                  <td className="mono muted">{door.firmwareVersion ?? de.app.none}</td>
                </tr>
                {door.status === 'stale' && (
                  <tr key={`${door.id}-note`} className="note-row">
                    <td />
                    <td colSpan={5}>
                      <div className="inner">
                        ⚠ {fmt(de.doors.lastFetch, { when: relative(door.lastSeenAt) })}
                        {' — '}{de.doors.staleExplain}
                      </div>
                    </td>
                  </tr>
                )}
                {!door.hasCodeKey && (
                  <tr key={`${door.id}-key`} className="note-row">
                    <td />
                    <td colSpan={5}><div className="inner">⚠ {de.doors.waitingExplain}</div></td>
                  </tr>
                )}
              </>
            ))}

            {(doors.data ?? []).length === 0 && (
              <tr><td colSpan={6} className="faint sm">{de.app.noEntries}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreateDoorDialog onClose={() => setShowCreate(false)}
                          onCreated={id => { doors.reload(); navigate(`/doors/${id}`) }}
                          onError={message => show(message, 'warn')} />
      )}

      {toast && <Toast message={toast.message} kind={toast.kind} onDone={clear} />}
    </>
  )
}

/**
 * Inbetriebnahme bleibt manuell (Entscheidung F3): Tür-ID und Schlüssel werden von der
 * seriellen Konsole abgelesen. Der Schlüssel verlässt den Controller damit nie über das Netz.
 */
function CreateDoorDialog({ onClose, onCreated, onError }: {
  onClose: () => void
  onCreated: (id: string) => void
  onError: (message: string) => void
}) {
  const [name, setName] = useState('')
  const [location, setLocation] = useState('')
  const [doorID, setDoorID] = useState('')
  const [codeKey, setCodeKey] = useState('')
  const [updateTime, setUpdateTime] = useState('03:00')
  const [busy, setBusy] = useState(false)

  const cleanKey = codeKey.trim().toLowerCase()
  const keyComplete = cleanKey.length === CODE_KEY_LENGTH && /^[0-9a-f]+$/.test(cleanKey)

  const submit = async () => {
    setBusy(true)
    try {
      const door = await api.post<Door>('/doors', {
        name, location: location || null, doorID: doorID.trim(), codeKey: cleanKey, updateTime,
      })
      onCreated(door.id)
    } catch (error) {
      onError(describeError(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgb(0 0 0 / .4)',
      display: 'grid', placeItems: 'center', zIndex: 60, padding: 'var(--sp-5)', overflow: 'auto',
    }} role="dialog" aria-modal="true">
      <div className="card" style={{ width: 520, boxShadow: 'var(--shadow-2)' }}>
        <div className="card-h">{de.doors.create}</div>
        <div className="card-b">
          <Field label={de.doors.name}>
            <input type="text" value={name} autoFocus onChange={event => setName(event.target.value)} />
          </Field>
          <Field label={de.doors.location}>
            <input type="text" value={location} onChange={event => setLocation(event.target.value)} />
          </Field>

          <hr style={{ border: 0, borderTop: '1px solid var(--border)', margin: 'var(--sp-4) 0' }} />

          <Field label={de.doors.doorID}>
            <input type="text" value={doorID} className="mono"
                   onChange={event => setDoorID(event.target.value)} />
          </Field>

          {/* Längenprüfung mit laufender Anzeige: ein zu kurz eingetippter Schlüssel äußert
              sich sonst erst später als 401 im Türlog, ohne Hinweis auf die Ursache. */}
          <Field label={de.doors.codeKey}
                 help={fmt(de.doors.codeKeyProgress, { entered: cleanKey.length })}
                 error={cleanKey.length > 0 && !keyComplete && cleanKey.length === CODE_KEY_LENGTH
                   ? de.errors.invalidCodeKey : null}>
            <input type="text" value={codeKey} className="mono"
                   onChange={event => setCodeKey(event.target.value)} />
          </Field>

          <Alert kind="info">{de.doors.serialHint}</Alert>

          <Field label={de.doors.updateTime} help={de.doors.updateTimeHint}>
            <input type="time" value={updateTime} className="mono" style={{ width: 110 }}
                   onChange={event => setUpdateTime(event.target.value)} />
          </Field>

          <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={onClose}>{de.app.cancel}</button>
            <button className="btn btn-primary"
                    disabled={busy || name.trim().length === 0 || doorID.trim().length === 0 || !keyComplete}
                    onClick={() => void submit()}>
              {de.doors.create}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export function DoorDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { toast, show, clear } = useToast()

  const door = useResource<Door>(`/doors/${id}`, [id])
  const status = useResource<DoorStatus>(`/doors/${id}/status`, [id])
  const bands = useResource<DoorScheduleBand[]>(`/doors/${id}/schedule`, [id])
  const groups = useResource<Group[]>('/groups')

  const [confirmDelete, setConfirmDelete] = useState(false)
  const [newKey, setNewKey] = useState('')
  const [showKeyField, setShowKeyField] = useState(false)

  if (door.loading) return <Spinner />
  if (door.error) return <ErrorBox error={door.error} onRetry={door.reload} />
  if (!door.data) return <ErrorBox error={new Error('not found')} />

  const detail = door.data

  const patch = async (body: Record<string, unknown>) => {
    try {
      await api.patch(`/doors/${id}`, body)
      door.reload()
      status.reload()
      show(de.app.save)
    } catch (error) {
      show(describeError(error), 'warn')
    }
  }

  const replaceKey = async () => {
    try {
      await api.put(`/doors/${id}/codekey`, { codeKey: newKey.trim().toLowerCase() })
      setNewKey('')
      setShowKeyField(false)
      door.reload()
      show(de.app.save)
    } catch (error) {
      show(describeError(error), 'warn')
    }
  }

  const remove = async () => {
    try {
      await api.delete(`/doors/${id}`)
      navigate('/doors')
    } catch (error) {
      setConfirmDelete(false)
      show(describeError(error), 'warn')
    }
  }

  const memberGroups = (groups.data ?? []).filter(group => group.doorIDs.includes(id))

  return (
    <>
      <PageHeader title={detail.name} subtitle={detail.location ?? undefined}
                  back={{ to: '/doors', label: de.doors.title }}
                  actions={<button className="btn btn-danger" onClick={() => setConfirmDelete(true)}>
                    {de.app.delete}
                  </button>} />

      {status.data?.approachingCodeLimit && (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <Alert kind="warning">
            {fmt(de.doors.codeLimitWarning, {
              count: status.data.deliveredCodeCount,
              max: status.data.maxCodeCount,
            })}
          </Alert>
        </div>
      )}

      {(status.data?.droppedCodeIDs.length ?? 0) > 0 && (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <Alert kind="warning">
            {fmt(de.doors.droppedCodes, { count: status.data!.droppedCodeIDs.length })}
          </Alert>
        </div>
      )}

      <div className="grid cols-2" style={{ marginBottom: 'var(--sp-4)' }}>
        <Card title={de.doors.status}>
          <dl className="kv">
            <dt>{de.doors.columnStatus}</dt>
            <dd><DoorStatusBadge status={status.data?.status ?? 'waiting'}
                                 lastSeenAt={detail.lastSeenAt} /></dd>

            <dt>{de.doors.lastFetch.replace(' {when}', '')}</dt>
            <dd className="mono">{formatDateTime(detail.lastSeenAt)}</dd>

            <dt>{de.doors.columnFirmware}</dt>
            <dd className="mono">{detail.firmwareVersion ?? de.app.none}</dd>

            <dt>{de.doors.payloadVersion}</dt>
            <dd className="mono faint">{status.data?.currentPayloadVersion ?? '…'}</dd>

            <dt>{de.doors.deliveredCodes}</dt>
            <dd className="mono">
              {status.data?.deliveredCodeCount ?? '…'} / {status.data?.maxCodeCount ?? 500}
            </dd>

            <dt>{de.doors.doorID}</dt>
            <dd className="mono faint">{detail.id}</dd>

            <dt>{de.doors.codeKey}</dt>
            <dd>
              {detail.hasCodeKey
                ? <span className="status status-ok">✓ {de.doors.codeKeySet}</span>
                : <span className="status status-warn">⚠ {de.app.none}</span>}
            </dd>
          </dl>

          {/* Reine Anzeige: die Firmware aktualisiert sich selbst. */}
          <div className="disabled-hint" style={{ marginTop: 'var(--sp-3)' }}>
            ⓘ {de.doors.firmwareReadOnly}
          </div>
        </Card>

        <Card title="Stammdaten">
          <Field label={de.doors.name}>
            <input type="text" defaultValue={detail.name}
                   onBlur={event => {
                     if (event.target.value !== detail.name) void patch({ name: event.target.value })
                   }} />
          </Field>

          <Field label={de.doors.location}>
            <input type="text" defaultValue={detail.location ?? ''}
                   onBlur={event => void patch({ location: event.target.value })} />
          </Field>

          <Field label={de.doors.updateTime} help={de.doors.updateTimeHint}>
            <input type="time" defaultValue={detail.updateTime} className="mono" style={{ width: 110 }}
                   onBlur={event => void patch({ updateTime: event.target.value })} />
          </Field>

          <Field label="Status">
            <label className="sm" style={{ display: 'flex', gap: 6 }}>
              <input type="checkbox" checked={detail.isActive}
                     onChange={event => void patch({ isActive: event.target.checked })} />
              {de.app.active}
            </label>
          </Field>

          {showKeyField ? (
            <Field label={de.doors.replaceCodeKey}
                   help={fmt(de.doors.codeKeyProgress, { entered: newKey.trim().length })}>
              <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
                <input type="text" value={newKey} className="mono"
                       onChange={event => setNewKey(event.target.value)} />
                <button className="btn btn-primary"
                        disabled={newKey.trim().length !== CODE_KEY_LENGTH}
                        onClick={() => void replaceKey()}>{de.app.save}</button>
                <button className="btn btn-ghost" onClick={() => setShowKeyField(false)}>
                  {de.app.cancel}
                </button>
              </div>
            </Field>
          ) : (
            <button className="btn btn-ghost" onClick={() => setShowKeyField(true)}>
              {de.doors.replaceCodeKey}
            </button>
          )}
        </Card>
      </div>

      <div style={{ marginBottom: 'var(--sp-4)' }}>
        <Card title={de.doors.columnGroups}>
          {memberGroups.length === 0
            ? <div className="faint sm">{de.app.noEntries}</div>
            : <table><tbody>
                {memberGroups.map(group => (
                  <tr key={group.id} className="clickable" onClick={() => navigate(`/groups/${group.id}`)}>
                    <td>
                      <span className="chip" style={{ color: group.color }}>
                        <span>{group.name}</span>
                      </span>
                    </td>
                    <td className="sm muted">
                      {group.isAlwaysOpen ? de.groups.always : group.rules.length + ' Zeitfenster'}
                    </td>
                    <td className="sm muted">{group.doorCodeCount} Türcodes</td>
                  </tr>
                ))}
              </tbody></table>}
        </Card>
      </div>

      <Card title={de.doors.whoWhen}>
        {(bands.data ?? []).length === 0
          ? <div className="faint sm">{de.app.noEntries}</div>
          : <>
              <DoorScheduleBands bands={bands.data!} />
              <div className="help" style={{ marginTop: 'var(--sp-2)' }}>{de.doors.whoWhenHint}</div>
            </>}
      </Card>

      {confirmDelete && (
        <ConfirmDialog
          title={fmt(de.doors.confirmDelete, { name: detail.name })}
          detail={de.doors.confirmDeleteDetail}
          confirmLabel={de.app.delete}
          onConfirm={() => void remove()}
          onCancel={() => setConfirmDelete(false)} />
      )}

      {toast && <Toast message={toast.message} kind={toast.kind} onDone={clear} />}
    </>
  )
}
